import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles, tokens, mergeClasses,
  Body1, Caption1, Spinner, Button, SearchBox,
  Dropdown, Option, Field,
  DataGrid, DataGridHeader, DataGridHeaderCell, DataGridBody, DataGridRow, DataGridCell,
  createTableColumn,
  type TableColumnDefinition,
} from '@fluentui/react-components';
import { LayerRegular, DataTrendingRegular } from '@fluentui/react-icons';
import { useMyProjects, useEpics, useEpicTree } from '../../api/hooks';
import { statusHue } from '../../components/common/StatusPill';
import { HeroProgressMetrics } from '../../components/common/HeroProgressMetrics';
import {
  rollup, resolveProgress, sumProgressInputs, progressBasisLabel, formatPoints,
} from './EpicProgressPage';
import { brandColors, brandFonts } from '../../theme/brandColors';

/* Feature Reports — same executive layout as the Epics page (navy hero + table),
   but lists the FEATURES of an Epic selected from dropdowns (Project → Epic).
   Feature rows come from rollup(epicTree).features (shared with Epic Progress). */

type FeatureRow = ReturnType<typeof rollup>['features'][number];
type Hue = 'green' | 'lightBlue' | 'purple' | 'blue' | 'red' | 'grey';
const HUE_SOLID: Record<Hue, string> = {
  green: brandColors.green, lightBlue: brandColors.lightBlue, purple: brandColors.purple,
  blue: brandColors.blue, red: brandColors.red, grey: brandColors.greyText,
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, width: '100%', fontFamily: brandFonts.body },

  hero: {
    backgroundColor: brandColors.navy, color: brandColors.white,
    borderRadius: tokens.borderRadiusXLarge, padding: '2.125rem 2.375rem 2rem',
    boxShadow: tokens.shadow28, overflow: 'hidden',
  },
  kicker: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, color: brandColors.lightBlue,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: tokens.spacingVerticalM,
  },
  heroTitle: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero800, lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold, letterSpacing: '-0.01em', margin: 0,
  },
  heroGrid: {
    display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: tokens.spacingHorizontalXXXL, alignItems: 'center',
    marginTop: tokens.spacingVerticalXXL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr', rowGap: tokens.spacingVerticalXL },
  },
  heroBars: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  barRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  barTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  barLabel: { fontSize: tokens.fontSizeBase300, color: 'rgba(255,255,255,0.92)' },
  barVal: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },
  heroTrack: { height: '0.375rem', borderRadius: tokens.borderRadiusCircular, backgroundColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' },
  heroFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },
  composite: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS,
    borderLeft: '1px solid rgba(255,255,255,0.14)', paddingLeft: tokens.spacingHorizontalXXXL,
    '@media (max-width: 860px)': { borderLeft: 'none', paddingLeft: 0 },
  },
  compositeLabel: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: brandColors.lightBlue,
  },
  compositeValue: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  compositeNum: { fontFamily: brandFonts.heading, fontSize: '3.5rem', fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  compositePct: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, color: brandColors.lightBlue },
  compositeMeta: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },

  // Epic selectors
  selectors: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalL, alignItems: 'flex-end' },
  selectField: { minWidth: '18rem' },

  container: {
    display: 'flex', flexDirection: 'column',
    backgroundColor: brandColors.white,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${brandColors.greyLine}`,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM, padding: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${brandColors.greyLine}`,
  },
  search: { width: '24rem', maxWidth: '50%' },
  gridWrap: {
    overflow: 'auto',
    '& [role="row"] > :first-child': { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
    '& [role="row"] > :nth-child(2)': { flexGrow: 0, flexShrink: 0, flexBasis: '9rem' },   // State
    '& [role="row"] > :nth-child(3)': { flexGrow: 0, flexShrink: 0, flexBasis: '9rem' },   // Stories
    '& [role="row"] > :nth-child(4)': { flexGrow: 0, flexShrink: 0, flexBasis: '15rem' },  // Progress
    '& [role="columnheader"]': {
      fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.1em', textTransform: 'uppercase', color: brandColors.greyText,
    },
  },
  rowClickable: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    '&:focus-visible': { outline: `2px solid ${brandColors.blue}`, outlineOffset: '-2px' },
  },
  titleCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  titleText: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.01em', color: brandColors.navy,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  idText: {
    fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, letterSpacing: '0.02em', color: brandColors.greyText,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  metaText: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: brandColors.greyText },
  pill: {
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  progressCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: '11rem' },
  progressTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  progressFrac: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: brandColors.greyText },
  progressPct: { fontFamily: brandFonts.heading, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy },
  barTrack: { width: '100%', height: '0.375rem', borderRadius: tokens.borderRadiusCircular, overflow: 'hidden', backgroundColor: brandColors.greyLine },
  barFill: { height: '100%', backgroundColor: brandColors.blue, borderRadius: tokens.borderRadiusCircular },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${brandColors.greyLine}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.1em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  emptyOrError: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalXXXL, textAlign: 'center', color: tokens.colorNeutralForeground2,
  },
});

// Weighted progress for one feature row — the SAME figure the hero totals, so a row can never
// contradict the number above it.
const featureProgress = (f: FeatureRow) => resolveProgress(f.progress);

export function FeatureListPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [epicId, setEpicId] = useState<number | undefined>(undefined);

  const { data: projects, isLoading: loadingProjects } = useMyProjects();
  // Default to the first accessible project.
  useEffect(() => {
    if (!projectId && projects && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const { data: epics, isLoading: loadingEpics } = useEpics(projectId);
  // Default to the first epic of the selected project; reset when project changes.
  useEffect(() => { setEpicId(undefined); }, [projectId]);
  useEffect(() => {
    if (epicId === undefined && epics && epics.length > 0) setEpicId(epics[0].id);
  }, [epics, epicId]);

  const { data: tree, isLoading: loadingTree, error, refetch } = useEpicTree(projectId, epicId);
  const r = useMemo(() => (tree ? rollup(tree) : null), [tree]);

  const project = projects?.find((p) => p.id === projectId);
  const selectedEpic = epics?.find((e) => e.id === epicId);

  const features = r?.features ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return features;
    return features.filter((f) =>
      (f.customerTitle ?? f.name).toLowerCase().includes(q) ||
      f.name.toLowerCase().includes(q) ||
      f.code.toLowerCase().includes(q) ||
      f.state.toLowerCase().includes(q),
    );
  }, [features, search]);

  // Hero: features grouped by state + overall story-completion across the features.
  const breakdown = useMemo(() => {
    const groups = new Map<string, number>();
    for (const f of filtered) groups.set(f.state, (groups.get(f.state) ?? 0) + 1);
    const total = filtered.length || 1;
    return [...groups.entries()]
      .map(([state, count]) => ({ state, count, pct: count / total, hue: statusHue(state) as Hue }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filtered]);
  const rollupStories = useMemo(() => {
    const closed = filtered.reduce((a, f) => a + f.closed, 0);
    const resolved = filtered.reduce((a, f) => a + f.resolved, 0);
    const total = filtered.reduce((a, f) => a + (f.total - f.removed), 0);
    const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
    // Sum each feature's raw progress ingredients, then divide once — see sumProgressInputs.
    const progress = resolveProgress(sumProgressInputs(filtered.map((f) => f.progress)));
    return { closed, resolved, total, progress, pctClosed: pct(closed), pctResolved: pct(resolved) };
  }, [filtered]);

  const columns: TableColumnDefinition<FeatureRow>[] = useMemo(() => [
    createTableColumn<FeatureRow>({
      columnId: 'title',
      compare: (a, b) => (a.customerTitle?.trim() || a.name).localeCompare(b.customerTitle?.trim() || b.name),
      renderHeaderCell: () => 'Feature',
      renderCell: (f) => (
        <div className={styles.titleCell}>
          <span className={styles.titleText}>{f.customerTitle?.trim() || f.name}</span>
          <span className={styles.idText}>#{f.id} · {f.code}</span>
        </div>
      ),
    }),
    createTableColumn<FeatureRow>({
      columnId: 'state',
      compare: (a, b) => a.state.localeCompare(b.state),
      renderHeaderCell: () => 'State',
      renderCell: (f) => {
        const c = HUE_SOLID[statusHue(f.state) as Hue];
        return <span className={styles.pill} style={{ backgroundColor: `${c}29`, color: c }}>{(f.state || 'Unknown').trim()}</span>;
      },
    }),
    createTableColumn<FeatureRow>({
      columnId: 'stories',
      compare: (a, b) => a.total - b.total,
      renderHeaderCell: () => 'Stories',
      renderCell: (f) => <span className={styles.metaText}>{f.total - f.removed}{f.removed ? ` (+${f.removed} cut)` : ''}</span>,
    }),
    createTableColumn<FeatureRow>({
      columnId: 'progress',
      compare: (a, b) => featureProgress(a).percent - featureProgress(b).percent,
      renderHeaderCell: () => 'Progress',
      renderCell: (f) => {
        const p = featureProgress(f);
        // Show the fraction the percentage is built from — a story count beside a points-weighted
        // % reads as a contradiction.
        const frac = p.basis === 'points'
          ? `${formatPoints(f.progress.adjustedPoints)} / ${formatPoints(f.progress.totalPoints)} pts`
          : `${formatPoints(f.progress.adjustedCount)} / ${f.progress.storyCount} stories`;
        return (
          <div className={styles.progressCell}>
            <div className={styles.progressTop}>
              <span className={styles.progressFrac}>{frac}</span>
              <span className={styles.progressPct}>{p.percent}%</span>
            </div>
            <div
              className={styles.barTrack}
              role="img"
              aria-label={`${p.percent}% complete, ${progressBasisLabel(p)}`}
            >
              <div className={styles.barFill} style={{ width: `${p.percent}%` }} />
            </div>
          </div>
        );
      },
    }),
  ], [styles]);

  const heroTitle = selectedEpic?.customerTitle?.trim() || selectedEpic?.title || 'Feature Reports';
  const total = features.length;
  const shown = filtered.length;
  const busy = loadingProjects || loadingEpics || loadingTree;

  return (
    <div className={styles.page}>
      {/* Navy hero */}
      <section className={styles.hero}>
        <div className={styles.kicker}><DataTrendingRegular fontSize={14} aria-hidden /> Feature Reports</div>
        <h1 className={styles.heroTitle}>{heroTitle}</h1>
        <div className={styles.heroGrid}>
          <div className={styles.heroBars}>
            {breakdown.map((b) => (
              <div key={b.state} className={styles.barRow}>
                <div className={styles.barTop}>
                  <span className={styles.barLabel}>{b.state}</span>
                  <span className={styles.barVal}>{b.count} · {Math.round(b.pct * 100)}%</span>
                </div>
                <div className={styles.heroTrack}>
                  <div className={styles.heroFill} style={{ width: `${b.pct * 100}%`, backgroundColor: HUE_SOLID[b.hue] }} />
                </div>
              </div>
            ))}
            {breakdown.length === 0 && <span className={styles.barLabel}>Select an epic to see its features.</span>}
          </div>
          <HeroProgressMetrics
            percentProgress={rollupStories.progress.percent}
            percentClosed={rollupStories.pctClosed}
            percentResolved={rollupStories.pctResolved}
            progressMeta={`${progressBasisLabel(rollupStories.progress)} · ${total} feature${total === 1 ? '' : 's'}`}
            closedMeta={`${rollupStories.closed} / ${rollupStories.total} closed`}
            resolvedMeta={`${rollupStories.resolved} / ${rollupStories.total} resolved`}
            unit="user stories"
          />
        </div>
      </section>

      {/* Project + Epic selectors */}
      <div className={styles.selectors}>
        <Field label="Project" className={styles.selectField}>
          <Dropdown
            placeholder="Select a project"
            value={project?.name ?? ''}
            selectedOptions={projectId ? [projectId] : []}
            onOptionSelect={(_e, d) => setProjectId(d.optionValue)}
            disabled={loadingProjects || !projects?.length}
          >
            {projects?.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
          </Dropdown>
        </Field>
        <Field label="Epic" className={styles.selectField}>
          <Dropdown
            placeholder={loadingEpics ? 'Loading epics…' : 'Select an epic'}
            value={selectedEpic ? (selectedEpic.customerTitle?.trim() || selectedEpic.title) : ''}
            selectedOptions={epicId !== undefined ? [String(epicId)] : []}
            onOptionSelect={(_e, d) => setEpicId(d.optionValue ? Number(d.optionValue) : undefined)}
            disabled={loadingEpics || !epics?.length}
          >
            {epics?.map((e) => (
              <Option key={e.id} value={String(e.id)}>{e.customerTitle?.trim() || e.title}</Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      <div className={styles.container} role="region" aria-label="Features list">
        <div className={styles.toolbar}>
          <SearchBox
            appearance="underline"
            className={styles.search}
            placeholder="Search features…"
            value={search}
            onChange={(_e, d) => setSearch(d.value)}
            aria-label="Search features"
          />
        </div>

        {busy ? (
          <div className={styles.emptyOrError}><Spinner size="small" label="Loading features…" /></div>
        ) : error ? (
          <div className={styles.emptyOrError}>
            <Body1>Couldn't load features.</Body1>
            <Caption1>{(error as Error).message}</Caption1>
            <Button appearance="primary" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : !projects?.length ? (
          <div className={styles.emptyOrError}><Body1>No projects accessible to you yet.</Body1></div>
        ) : !epicId ? (
          <div className={styles.emptyOrError}>
            <LayerRegular fontSize={32} />
            <Body1>Select an epic to see its features.</Body1>
          </div>
        ) : total === 0 ? (
          <div className={styles.emptyOrError}>
            <LayerRegular fontSize={32} />
            <Body1>No features under this epic.</Body1>
          </div>
        ) : shown === 0 ? (
          <div className={styles.emptyOrError}>
            <Body1>No features match "{search}".</Body1>
            <Button appearance="subtle" onClick={() => setSearch('')}>Clear search</Button>
          </div>
        ) : (
          <div className={styles.gridWrap}>
            <DataGrid items={filtered} columns={columns} sortable focusMode="row_unstable" getRowId={(f) => f.id}>
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<FeatureRow>>
                {({ item, rowId }) => {
                  const go = () => navigate(`/reports/projects/${projectId}/epics/${epicId}/features/${item.id}/progress`);
                  return (
                    <DataGridRow<FeatureRow>
                      key={rowId}
                      className={mergeClasses(styles.rowClickable)}
                      onClick={go}
                      onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) => {
                        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); }
                      }}
                      aria-label={`Open ${item.name} in Feature Progress`}
                    >
                      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                    </DataGridRow>
                  );
                }}
              </DataGridBody>
            </DataGrid>
          </div>
        )}

        {!busy && !error && total > 0 && (
          <div className={styles.footer}>
            <span>
              {shown === total
                ? `${total} feature${total === 1 ? '' : 's'}`
                : `Showing ${shown} of ${total} feature${total === 1 ? '' : 's'}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
